/* eslint no-lonely-if: "warn" */
import { useEffect } from 'react'
import { Session } from 'next-auth'
import env from '@beam-australia/react-env'
import { signOut } from 'next-auth/react'

// Note: we do these complex redirects client-side not because we want to, but because
// next-auth doesn't really support multiple tenant subdomains (it has an idea of a single
// NEXTAUTH_URL).

const useRedirects = (session: undefined | Session, sessionLoading: boolean): void => {
  useEffect(() => {
    // handles a user being logged in from before
    // But their privileges hav been revoked, for instance if an employee
    // leaves an insitution
    if (session && !session.user.tenantData) {
      signOut({ callbackUrl: window.location.origin })
      return
    }

    const rootURL = new URL(env('ROOT_URL'))
    const isTopLevel = window.location.host === rootURL.host
    const isAdminRoute = window.location.pathname.includes('/admin')
    const isDocsRoute = window.location.pathname.includes('/docs')
    const isExternalAlias = !window.location.host.includes(rootURL.host)

    // Docs can load on top level
    if (isDocsRoute) {
      window.location.href = `${rootURL.protocol}//docs.accesskit.cc`
      return
    }

    // because NextAuth can't create a cookie for the external alias
    // we push custom domain admin logins to default domain admin,
    // which will find the default subdoamin when useTopLevelRedirect fires again
    if (isExternalAlias && isAdminRoute) {
      window.location.href = `${rootURL.protocol}//${rootURL.host}/admin`
      return
    }

    // Otherwise we need to wait for the session to finish
    // loading before decidig wait to do
    if (sessionLoading) {
      return
    }

    // This function checks if a logged in user is on the incorrect dashboard.
    const accessingOtherTenantsDashboard = (sesh: NonNullable<Session>) => {
      // If no session, login screen will show
      if (!sesh) return false
      const subdomain = window.location.host.split('.')[0]
      const isOtherTenantsSubdomain = subdomain !== sesh.user.tenantData.tenantSubdomain
      return isOtherTenantsSubdomain && isAdminRoute
    }

    const adminDashboardPath = session?.user.tenantData?.tenantSubdomain ? `${rootURL.protocol}//${session.user.tenantData.tenantSubdomain}.${rootURL.host}/admin` : `${rootURL.protocol}//${rootURL.host}/admin`

    // In solo tenant mode, we don't redirect any urls
    // except for externalAlias admin routes, handled above
    // and admin routes for tenants other than the logged in tenant/top level
    if (env('SOLO_TENANT')) {
      if (!isTopLevel && accessingOtherTenantsDashboard(session)) {
        window.location.href = adminDashboardPath
      }
      // All other routes don't get a redirect
    } else if (session) {
      // Handle redirecting logged in users
      // They get redirected to the admin dashboard on toplevel routes and admin routes for other tenants
      // Top level routes and incorrect admin routes get redirected to the correct dashboard
      if (isTopLevel || accessingOtherTenantsDashboard(session)) {
        window.location.href = adminDashboardPath
      }
      // Otherwise, they are logged in to the correct dashboard or visiting a non-admin route, so no redirect neded.
    } else if (isTopLevel && !isAdminRoute) {
      // Handle redirecting anon users
      // They get redirected to the marketing page unless they are visiting an admin route
      // Top level non-admin routes show the admin page
      window.location.href = 'https://accesskit.media'
    }
    // top level admin routes will show the admin page
    // top level normal routes will render normally
  }, [session, sessionLoading])
}

export default useRedirects
